import { rect, aRect, px } from '../../../pixel/canvas';
import { SLOT } from '../slots';
import type { Part } from '../../types';

// Anchor = face center (x=32, y varies by body ~13)
// Eyes typically at a.y-1, mouth at a.y+3

export const faces: Record<string, Part> = {
  face_neutral: {
    id: 'face_neutral', category: 'face', name: 'Neutral', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      rect(ctx, a.x - 4, a.y - 1, 2, 2, p.dark);    // left eye
      rect(ctx, a.x + 2, a.y - 1, 2, 2, p.dark);    // right eye
      rect(ctx, a.x - 2, a.y + 3, 4, 1, p.dark);    // mouth
    },
  },
  face_happy: {
    id: 'face_happy', category: 'face', name: 'Happy', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      rect(ctx, a.x - 4, a.y - 1, 2, 2, p.dark);    // left eye
      rect(ctx, a.x + 2, a.y - 1, 2, 2, p.dark);    // right eye
      // Smile (U shape)
      px(ctx, a.x - 2, a.y + 3, p.dark);
      rect(ctx, a.x - 1, a.y + 4, 4, 1, p.dark);
      px(ctx, a.x + 3, a.y + 3, p.dark);
      // Rosy cheeks
      px(ctx, a.x - 5, a.y + 1, p.accent);
      px(ctx, a.x + 4, a.y + 1, p.accent);
    },
  },
  face_cool: {
    id: 'face_cool', category: 'face', name: 'Cool', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      // Sunglasses frame
      rect(ctx, a.x - 5, a.y - 2, 12, 1, p.dark);   // bridge+frame
      rect(ctx, a.x - 5, a.y - 1, 4, 2, p.accentDark); // left lens
      rect(ctx, a.x + 1, a.y - 1, 4, 2, p.accentDark); // right lens
      px(ctx, a.x - 4, a.y - 1, p.accent);           // left lens shine
      px(ctx, a.x + 2, a.y - 1, p.accent);           // right lens shine
      rect(ctx, a.x - 2, a.y + 3, 4, 1, p.dark);     // smirk
    },
  },
  face_angry: {
    id: 'face_angry', category: 'face', name: 'Angry', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      // Angled brows
      rect(ctx, a.x - 5, a.y - 3, 3, 1, p.dark);
      px(ctx, a.x - 3, a.y - 4, p.dark);
      px(ctx, a.x + 2, a.y - 4, p.dark);
      rect(ctx, a.x + 2, a.y - 3, 3, 1, p.dark);
      // Narrow eyes
      rect(ctx, a.x - 4, a.y - 1, 2, 1, p.dark);
      rect(ctx, a.x + 2, a.y - 1, 2, 1, p.dark);
      // Frown
      px(ctx, a.x - 2, a.y + 4, p.dark);
      rect(ctx, a.x - 1, a.y + 3, 4, 1, p.dark);
      px(ctx, a.x + 3, a.y + 4, p.dark);
    },
  },
  face_sad: {
    id: 'face_sad', category: 'face', name: 'Sad', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      // Droopy eyes
      rect(ctx, a.x - 4, a.y, 2, 2, p.dark);
      rect(ctx, a.x + 2, a.y, 2, 2, p.dark);
      // Sad brows (angled down inward)
      px(ctx, a.x - 4, a.y - 2, p.dark);
      px(ctx, a.x - 3, a.y - 3, p.dark);
      px(ctx, a.x + 3, a.y - 3, p.dark);
      px(ctx, a.x + 4, a.y - 2, p.dark);
      // Frown
      px(ctx, a.x - 2, a.y + 4, p.dark);
      rect(ctx, a.x - 1, a.y + 3, 4, 1, p.dark);
      px(ctx, a.x + 3, a.y + 4, p.dark);
      // Tear
      px(ctx, a.x - 3, a.y + 2, p.glass);
      px(ctx, a.x - 3, a.y + 3, p.glass);
    },
  },
  face_wink: {
    id: 'face_wink', category: 'face', name: 'Wink', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      rect(ctx, a.x - 4, a.y - 1, 2, 2, p.dark);    // left eye open
      // Right eye winking (line)
      rect(ctx, a.x + 2, a.y, 2, 1, p.dark);
      px(ctx, a.x + 1, a.y - 1, p.dark);
      px(ctx, a.x + 4, a.y - 1, p.dark);
      // Smile
      px(ctx, a.x - 2, a.y + 3, p.dark);
      rect(ctx, a.x - 1, a.y + 4, 4, 1, p.dark);
      px(ctx, a.x + 3, a.y + 3, p.dark);
    },
  },
  face_mask: {
    id: 'face_mask', category: 'face', name: 'Mask', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      rect(ctx, a.x - 4, a.y - 1, 2, 2, p.dark);    // left eye
      rect(ctx, a.x + 2, a.y - 1, 2, 2, p.dark);    // right eye
      // Mask covering lower face
      aRect(ctx, a.x, a.y + 2, 10, 5, p.light);
      aRect(ctx, a.x, a.y + 2, 10, 1, p.shadow);    // mask top edge
      // Mask crease lines
      rect(ctx, a.x - 4, a.y + 4, 2, 1, p.shadow);
      rect(ctx, a.x + 2, a.y + 4, 2, 1, p.shadow);
    },
  },
  face_robot: {
    id: 'face_robot', category: 'face', name: 'Robot', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      // Visor/screen
      aRect(ctx, a.x, a.y - 1, 10, 3, p.accentDark);
      // Glowing eye pixels
      px(ctx, a.x - 3, a.y, p.accent);
      px(ctx, a.x - 2, a.y, p.accentLight);
      px(ctx, a.x + 1, a.y, p.accent);
      px(ctx, a.x + 2, a.y, p.accentLight);
      // Speaker grill
      aRect(ctx, a.x, a.y + 3, 8, 3, p.metalDark);
      px(ctx, a.x - 2, a.y + 4, p.metal);
      px(ctx, a.x, a.y + 4, p.metal);
      px(ctx, a.x + 2, a.y + 4, p.metal);
    },
  },
  face_glasses: {
    id: 'face_glasses', category: 'face', name: 'Glasses', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      // Round glasses frames
      rect(ctx, a.x - 5, a.y - 2, 4, 4, p.metalDark); // left frame
      rect(ctx, a.x + 1, a.y - 2, 4, 4, p.metalDark); // right frame
      rect(ctx, a.x - 4, a.y - 1, 2, 2, p.glassLight); // left lens
      rect(ctx, a.x + 2, a.y - 1, 2, 2, p.glassLight); // right lens
      px(ctx, a.x - 4, a.y - 1, p.glassLight);         // left shine
      px(ctx, a.x + 2, a.y - 1, p.glassLight);         // right shine
      px(ctx, a.x, a.y - 1, p.metalDark);               // bridge
      rect(ctx, a.x - 2, a.y + 3, 4, 1, p.dark);       // mouth
    },
  },
  face_surprised: {
    id: 'face_surprised', category: 'face', name: 'Surprised', requires: SLOT.FACE,
    draw: (ctx, a, p) => {
      // Wide eyes
      rect(ctx, a.x - 5, a.y - 2, 3, 3, p.dark);
      rect(ctx, a.x + 2, a.y - 2, 3, 3, p.dark);
      px(ctx, a.x - 4, a.y - 1, p.glassLight);         // left eye shine
      px(ctx, a.x + 3, a.y - 1, p.glassLight);         // right eye shine
      // Raised brows
      rect(ctx, a.x - 5, a.y - 4, 3, 1, p.dark);
      rect(ctx, a.x + 2, a.y - 4, 3, 1, p.dark);
      // O mouth
      aRect(ctx, a.x, a.y + 3, 4, 3, p.dark);
      aRect(ctx, a.x, a.y + 4, 2, 1, p.light);
    },
  },
};
